/**
 * Mock WebSocket publisher.
 *
 * Generates a realistic event stream against the seed graph and pushes it
 * through `wsManager.inject(...)` — bypassing the real WS connection. The
 * dashboard's blotter / P&L / assignment grid / rollout metrics / remediation
 * queue all subscribe to the same store, so the demo "feels live" without
 * needing Tier C3 to ship first.
 *
 * Tunable cadences (deliberately conservative for the foundation preview;
 * P3 will turn these up to exercise the 200 msgs/sec budget):
 *
 *   • pnl_tick                   — every 1.2s, ±0.4s jitter
 *   • fill                       — every  6s,   ±2s jitter
 *   • signal                     — every 18s,   ±5s jitter
 *   • trade_open / trade_close   — every 22s,   ±6s jitter
 *   • assignment_state_changed   — every 45s,   ±15s jitter
 *   • rollout_phase_metrics      — every 10s,   ±2s jitter
 *   • remediation_opened         — every 90s,   ±20s jitter
 *   • risk_violation             — every 75s,   ±20s jitter
 *
 * All cadences become irrelevant in test runs — `startMockWsPublisher()`
 * checks for NODE_ENV=test and refuses to start there.
 */

'use client';

import { wsManager } from '@/lib/ws/manager';
import { _unsafeIsoDateTime } from '@/types/money';
import type { WsAllEvents } from '@/types/events';
import type { ISODateTime } from '@/types/money';

import { getSeed } from './seed';

/* ============================================================================
 * Lifecycle (singleton, idempotent)
 * ============================================================================ */

let started = false;
let timers: Array<ReturnType<typeof setTimeout>> = [];

export const startMockWsPublisher = (): void => {
  if (started) {
    return;
  }
  if (typeof window === 'undefined') {
    return; // SSR — no-op
  }
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    return;
  }
  started = true;
  scheduleAll();
};

export const stopMockWsPublisher = (): void => {
  for (const t of timers) {
    clearTimeout(t);
  }
  timers = [];
  started = false;
};

/* ============================================================================
 * Helpers
 * ============================================================================ */

const nowIso = (): ISODateTime => _unsafeIsoDateTime(new Date().toISOString());

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);
const gauss = (mean: number, sd: number): number => {
  // Box-Muller
  const u1 = Math.max(Math.random(), 1e-9);
  const u2 = Math.random();
  return mean + sd * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
};
const round2 = (n: number): number => Math.round(n * 100) / 100;
const pick = <T>(xs: readonly T[]): T | undefined =>
  xs.length === 0 ? undefined : xs[Math.floor(Math.random() * xs.length)];

const scheduleNext = (
  fnName: string,
  meanMs: number,
  jitterMs: number,
  produce: () => WsAllEvents | null,
): void => {
  if (!started) {
    return;
  }
  const delay = Math.max(50, gauss(meanMs, jitterMs));
  const t = setTimeout(() => {
    if (!started) {
      return;
    }
    try {
      const event = produce();
      if (event !== null) {
        wsManager.inject(event);
      }
    } catch {
      /* an event-producer error must not break the loop */
    }
    scheduleNext(fnName, meanMs, jitterMs, produce);
  }, delay);
  timers.push(t);
};

const scheduleAll = (): void => {
  scheduleNext('pnl_tick', 1200, 400, producePnlTick);
  scheduleNext('fill', 6000, 2000, produceFill);
  scheduleNext('signal', 18_000, 5000, produceSignal);
  scheduleNext('trade_open_close', 22_000, 6000, produceTradeOpenOrClose);
  scheduleNext('assignment_state_changed', 45_000, 15_000, produceAssignmentStateChanged);
  scheduleNext('rollout_phase_metrics', 10_000, 2000, produceRolloutPhaseMetrics);
  scheduleNext('remediation_opened', 90_000, 20_000, produceRemediationOpened);
  scheduleNext('risk_violation', 75_000, 20_000, produceRiskViolation);
};

/* ============================================================================
 * Producers — each returns a single WS event or null if it can't synthesise
 * ============================================================================ */

const producePnlTick = (): WsAllEvents | null => ({
  type: 'pnl_tick',
  time: nowIso(),
  realized: round2(gauss(0, 4_000)),
  unrealized: round2(gauss(0, 2_500)),
  total: round2(gauss(0, 5_000)),
});

const produceFill = (): WsAllEvents | null => {
  const seed = getSeed();
  const trade = pick(seed.trades);
  if (trade === undefined) {
    return null;
  }
  return {
    type: 'fill',
    trade_id: trade.id,
    qty: trade.quantity,
    price: round2(trade.entry_price + gauss(0, 0.5)),
    time: nowIso(),
  };
};

const produceSignal = (): WsAllEvents | null => {
  const seed = getSeed();
  const sig = pick(seed.signals);
  if (sig === undefined) {
    return null;
  }
  return {
    type: 'signal',
    signal_id: sig.signal_id,
    strategy_id: sig.strategy_id,
    target_state: sig.target_state,
    emitted_at: nowIso(),
  };
};

const produceTradeOpenOrClose = (): WsAllEvents | null => {
  const seed = getSeed();
  const trade = pick(seed.trades);
  if (trade === undefined) {
    return null;
  }
  return Math.random() < 0.55
    ? { type: 'trade_open', trade }
    : { type: 'trade_close', trade: { ...trade, is_open: false } };
};

const produceAssignmentStateChanged = (): WsAllEvents | null => {
  const seed = getSeed();
  const a = pick(seed.assignments);
  if (a === undefined) {
    return null;
  }
  const transitions: Array<{ from: string; to: string; reason: string }> = [
    { from: 'PILOT', to: 'RAMPING', reason: 'Phase 1 metrics within tolerance — promoted.' },
    { from: 'RAMPING', to: 'ACTIVE', reason: 'Rollout reached 100% — completed.' },
    { from: 'ACTIVE', to: 'PAUSED_BY_OPERATOR', reason: 'Pre-event flatten requested.' },
    { from: 'ACTIVE', to: 'PAUSED_BY_MANDATE', reason: 'Mandate breach: exposure 12% > 10%.' },
    { from: 'PAUSED_BY_OPERATOR', to: 'ACTIVE', reason: 'Manual resume.' },
  ];
  const tr = transitions[Math.floor(Math.random() * transitions.length)] ?? transitions[0];
  if (tr === undefined) {
    return null;
  }
  const actor = pick(seed.operators);
  return {
    type: 'assignment_state_changed',
    assignment_id: a.id,
    from: tr.from,
    to: tr.to,
    at: nowIso(),
    reason: tr.reason,
    actor_id: actor?.id ?? 'system',
  };
};

const produceRolloutPhaseMetrics = (): WsAllEvents | null => {
  const seed = getSeed();
  const r = pick(seed.rollouts.filter((x) => x.status === 'RUNNING' || x.status === 'PAUSED'));
  if (r === undefined) {
    return null;
  }
  return {
    type: 'rollout_phase_metrics',
    rollout_id: r.id,
    phase_index: r.current_phase_index,
    metrics: {
      realized_dd_pct: round2(Math.abs(gauss(2, 1.5))),
      slippage_bps_avg: round2(Math.abs(gauss(15, 7))),
      fill_rate: round2(Math.min(1, Math.max(0.85, gauss(0.96, 0.02)))),
      error_count: Math.floor(Math.abs(gauss(0, 1.2))),
      members_active: r.phases[r.current_phase_index]?.metrics_snapshot?.members_active ?? 0,
    },
    at: nowIso(),
  };
};

const produceRemediationOpened = (): WsAllEvents | null => {
  const seed = getSeed();
  const a = pick(seed.assignments.filter((x) => x.state === 'ACTIVE'));
  if (a === undefined) {
    return null;
  }
  const slaMs = Date.now() + 24 * 60 * 60 * 1000;
  return {
    type: 'remediation_opened',
    item_id: `rem-live-${Date.now()}`,
    assignment_id: a.id,
    severity: pick(['medium', 'high', 'critical'] as const) ?? 'medium',
    sla_deadline: _unsafeIsoDateTime(new Date(slaMs).toISOString()),
    at: nowIso(),
  };
};

const produceRiskViolation = (): WsAllEvents | null => {
  const seed = getSeed();
  const rule = pick(seed.riskRules);
  if (rule === undefined) {
    return null;
  }
  return {
    type: 'risk_violation',
    violation: {
      id: Math.floor(Math.random() * 1_000_000),
      rule_id: rule.id,
      symbol: pick(['NIFTY', 'BANKNIFTY', 'RELIANCE', 'TCS']) ?? 'NIFTY',
      side: Math.random() < 0.5 ? 'buy' : 'sell',
      quantity: Math.floor(Math.abs(gauss(150, 80))),
      reason: `Mock violation against ${rule.kind}`,
      at: nowIso(),
    },
  };
};

/* ============================================================================
 * Burst helper — drives the high-throughput soak test in P3.
 * Exposed so the preview's "burst" button can stress the RAF batcher.
 * ============================================================================ */

export const burstEvents = (count: number): void => {
  if (!started) {
    return;
  }
  for (let i = 0; i < count; i++) {
    const ev = producePnlTick();
    if (ev !== null) {
      wsManager.inject(ev);
    }
  }
};

void rand; // reserved for future cadence tuning helpers
